import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { RaRecord, ShowProps } from "react-admin";
import {
  Confirm,
  ReferenceField,
  Show,
  TextField,
  Title,
  TopToolbar,
  useNotify,
  usePermissions,
  useRefresh,
  useShowContext,
} from "react-admin";
import Button from "@mui/material/Button";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import { Link as RouterLink, useParams } from "react-router-dom";

import { callApi, type CallApiParams } from "@/dataprovider/misc-apis";
import { formatDisplayDateTime, formatListDate } from "@/utils/date-format";
import { staffShowPath } from "@/containers/staff/staff/staff-show-helpers";
import { hasPermission } from "@/utils/permission-utils";
import type { PermissionRecord } from "@/types/permissions";

import {
  buildLeaveAdjustCall,
  buildLeaveAdjustmentReviewCall,
  buildLeaveReviewCall,
  canAdjustLeave,
  extractLeaveReviewRow,
  formatLeaveApproverRemarks,
  isDecidedLeaveStatus,
  isLeaveAlreadyProcessedError,
  isPortalOnlyLeaveAccess,
  leaveApiErrorMessage,
  leaveApproverRemarksLabel,
  leaveStatusChipColor,
  leaveTypeLabel,
  type LeaveAdjustmentAction,
  type LeavePendingAdjustment,
  type LeaveReviewAction,
} from "./leave-request-helpers";
import { AdjustLeaveDialog } from "./AdjustLeaveDialog";
import { LeaveBalanceAlert } from "./LeaveBalanceAlert";
import { leaveBalanceUiRefreshKey } from "@/containers/staff/shared/leave-balance";

type LeaveRecord = RaRecord & {
  id: string;
  status?: string;
  leaveType?: string;
  startDate?: string;
  endDate?: string;
  source?: string;
  reason?: string | null;
  employeeId?: number;
  reviewedAt?: string | null;
  reviewRemarks?: string | null;
  reviewedVia?: string | null;
  reviewedBy?: string | null;
  reviewedByName?: string | null;
  createdAt?: string;
  updatedAt?: string;
  leaveBalance?: unknown;
  balance?: unknown;
  pendingAdjustment?: LeavePendingAdjustment | null;
};

type LeaveShowMergeContextValue = {
  patchedRecord: LeaveRecord | null;
  mergeRecord: (row: LeaveRecord) => void;
};

const LeaveShowMergeContext = createContext<LeaveShowMergeContextValue | null>(null);

function useLeaveShowMerge(): LeaveShowMergeContextValue | null {
  return useContext(LeaveShowMergeContext);
}

function usePortalOnlyLeaveViewer(): boolean {
  const { permissions } = usePermissions<PermissionRecord>();
  return isPortalOnlyLeaveAccess(
    hasPermission(permissions, "employees.view"),
    hasPermission(permissions, "myAttendance.view")
  );
}

type LeaveActionsProps = {
  record: LeaveRecord;
  portalOnly?: boolean;
  onRecordUpdated?: (row: LeaveRecord) => void;
};

const LeaveActionsBar = ({ record, portalOnly = false, onRecordUpdated }: LeaveActionsProps) => {
  const { permissions } = usePermissions();
  const [action, setAction] = useState<LeaveReviewAction | null>(null);
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustmentAction, setAdjustmentAction] = useState<LeaveAdjustmentAction | null>(null);
  const remarksRef = useRef("");
  const notify = useNotify();
  const refresh = useRefresh();
  const leaveShowMerge = useLeaveShowMerge();

  const canEdit = hasPermission(permissions, "employees.edit");
  const status = record.status as string;
  const canDecide = !portalOnly && canEdit && status === "PENDING";
  const canCancel = (portalOnly || canEdit) && (status === "PENDING" || status === "APPROVED");
  const canRequestAdjust = (portalOnly || canEdit) && canAdjustLeave(record);
  const canDecideAdjustment = !portalOnly && canEdit && !!record.pendingAdjustment;

  const closeDialog = () => {
    setAction(null);
    setAdjustmentAction(null);
    setRemarks("");
    remarksRef.current = "";
  };

  const runAction = (kind: LeaveReviewAction) => {
    const snapshotRemarks = remarksRef.current;
    const callParams = buildLeaveReviewCall(kind, record.id, snapshotRemarks, portalOnly);

    setSubmitting(true);
    callApi(callParams)
      .then((response) => {
        const updatedRow = extractLeaveReviewRow(response.data);
        if (updatedRow) {
          const merged = {
            ...record,
            ...updatedRow,
            // Drop any snapshot balance so the alert reloads from the live endpoint.
            leaveBalance: undefined,
            balance: undefined,
          } as LeaveRecord;
          onRecordUpdated?.(merged);
          leaveShowMerge?.mergeRecord(merged);
        }
        notify(kind === "approve" ? "Approved" : kind === "decline" ? "Declined" : "Cancelled");
        if (!portalOnly) {
          refresh();
        }
      })
      .catch((err: unknown) => {
        if (isLeaveAlreadyProcessedError(err)) {
          refresh();
          notify("Leave was already reviewed — refreshed", { type: "info" });
          return;
        }
        notify(leaveApiErrorMessage(err, `Failed to ${kind}`), { type: "warning" });
      })
      .finally(() => {
        setSubmitting(false);
        closeDialog();
      });
  };

  const applyUpdatedRow = (updatedRow: Record<string, unknown> | null) => {
    if (!updatedRow) return;
    const merged = {
      ...record,
      ...updatedRow,
      leaveBalance: undefined,
      balance: undefined,
    } as LeaveRecord;
    onRecordUpdated?.(merged);
    leaveShowMerge?.mergeRecord(merged);
  };

  const runAdjustCall = (call: CallApiParams, successMessage: string, failMessage: string) => {
    setSubmitting(true);
    callApi(call)
      .then((response) => {
        applyUpdatedRow(extractLeaveReviewRow(response.data));
        notify(successMessage);
        if (!portalOnly) refresh();
        setAdjustOpen(false);
        closeDialog();
      })
      .catch((err: unknown) => {
        notify(leaveApiErrorMessage(err, failMessage), { type: "warning" });
      })
      .finally(() => setSubmitting(false));
  };

  const titles: Record<LeaveReviewAction, string> = {
    approve: "Approve leave request",
    decline: "Decline leave request",
    cancel: "Cancel leave request",
  };

  return (
    <TopToolbar>
      {canDecide ? (
        <>
          <Button onClick={() => setAction("approve")} color="success" startIcon={<DoneIcon />}>
            Approve
          </Button>
          <Button onClick={() => setAction("decline")} color="error" startIcon={<CloseIcon />}>
            Decline
          </Button>
        </>
      ) : null}
      {canCancel ? (
        <Button
          onClick={() => setAction("cancel")}
          color="inherit"
          startIcon={<CancelOutlinedIcon />}
        >
          Cancel request
        </Button>
      ) : null}

      {canRequestAdjust ? (
        <Button
          onClick={() => setAdjustOpen(true)}
          color="primary"
          startIcon={<EditCalendarIcon />}
        >
          Adjust dates
        </Button>
      ) : null}
      {canDecideAdjustment ? (
        <>
          <Button
            onClick={() => setAdjustmentAction("approve")}
            color="success"
            startIcon={<DoneIcon />}
          >
            Approve adjustment
          </Button>
          <Button
            onClick={() => setAdjustmentAction("decline")}
            color="error"
            startIcon={<CloseIcon />}
          >
            Decline adjustment
          </Button>
        </>
      ) : null}

      <AdjustLeaveDialog
        open={adjustOpen}
        submitting={submitting}
        startDate={record.startDate ?? ""}
        endDate={record.endDate ?? ""}
        onClose={() => setAdjustOpen(false)}
        onSubmit={(dates, reason) =>
          runAdjustCall(
            buildLeaveAdjustCall(record.id, dates, reason, portalOnly),
            "Adjustment requested — awaiting approval",
            "Failed to request adjustment"
          )
        }
      />

      <Confirm
        isOpen={adjustmentAction != null}
        loading={submitting}
        title={
          adjustmentAction === "approve" ? "Approve date adjustment" : "Decline date adjustment"
        }
        content={
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {adjustmentAction === "approve"
                ? "Apply the requested dates? Attendance overlay and paid balance follow the new dates."
                : "Decline the request? The originally approved dates stay."}
            </Typography>
            <MuiTextField
              label="Remarks (optional)"
              value={remarks}
              onChange={(e) => {
                setRemarks(e.target.value);
                remarksRef.current = e.target.value;
              }}
              fullWidth
              multiline
              minRows={2}
              size="small"
              disabled={submitting}
            />
          </Box>
        }
        onConfirm={() => {
          if (adjustmentAction && !submitting) {
            runAdjustCall(
              buildLeaveAdjustmentReviewCall(adjustmentAction, record.id, remarksRef.current),
              adjustmentAction === "approve" ? "Adjustment approved" : "Adjustment declined",
              `Failed to ${adjustmentAction} adjustment`
            );
          }
        }}
        onClose={() => {
          if (!submitting) closeDialog();
        }}
      />

      <Confirm
        isOpen={action != null}
        loading={submitting}
        title={action ? titles[action] : ""}
        content={
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {action === "approve"
                ? "Approve this leave? It will show as L on the attendance grid."
                : action === "decline"
                  ? "Decline this leave request?"
                  : "Cancel this leave request? It will no longer overlay attendance as leave."}
            </Typography>
            <MuiTextField
              label="Remarks (optional)"
              value={remarks}
              onChange={(e) => {
                const next = e.target.value;
                setRemarks(next);
                remarksRef.current = next;
              }}
              fullWidth
              multiline
              minRows={2}
              size="small"
              disabled={submitting}
            />
          </Box>
        }
        onConfirm={() => {
          if (action && !submitting) runAction(action);
        }}
        onClose={() => {
          if (!submitting) closeDialog();
        }}
      />
    </TopToolbar>
  );
};

const OfficeLeaveActions = () => {
  const { record } = useShowContext<LeaveRecord>();
  if (!record) return null;
  return <LeaveActionsBar record={record} />;
};

const InfoSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <Paper
    elevation={1}
    sx={{
      p: 2,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Typography
      variant="h6"
      sx={{
        mb: 1.5,
        pb: 0.75,
        borderBottom: "2px solid",
        borderColor: "primary.main",
        fontWeight: 600,
        color: "text.primary",
        fontSize: "1.1rem",
      }}
    >
      {title}
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>{children}</Box>
  </Paper>
);

const InfoField = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 600, fontSize: "0.75rem" }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.25 }} component="div">
      {value != null && value !== "" ? value : "—"}
    </Typography>
  </Box>
);

const LeaveRequestShowBody = ({
  record,
  portalOnly = false,
}: {
  record: LeaveRecord;
  portalOnly?: boolean;
}) => {
  const remarksLabel = leaveApproverRemarksLabel(portalOnly);
  const showDecidedRemarks =
    isDecidedLeaveStatus(record.status as string) && record.reviewedAt != null;

  return (
    <Box sx={{ p: 1 }}>
      <LeaveBalanceAlert
        employeeId={record.employeeId}
        mode={portalOnly ? "self" : "office"}
        refreshKey={leaveBalanceUiRefreshKey({
          status: record.status,
          updatedAt: record.updatedAt,
        })}
      />
      {record.pendingAdjustment ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Date adjustment awaiting approval:</strong>{" "}
          {formatListDate(record.startDate, { empty: "—" })} –{" "}
          {formatListDate(record.endDate, { empty: "—" })} →{" "}
          {formatListDate(record.pendingAdjustment.startDate, { empty: "—" })} –{" "}
          {formatListDate(record.pendingAdjustment.endDate, { empty: "—" })}
          {record.pendingAdjustment.reason ? ` (${record.pendingAdjustment.reason})` : ""}. The
          approved dates apply until it is accepted.
        </Alert>
      ) : null}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <InfoSection title="Request">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              >
                Status
              </Typography>
              <Chip
                label={String(record.status)}
                color={leaveStatusChipColor(record.status as string)}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>
            {showDecidedRemarks ? (
              <InfoField
                label={remarksLabel}
                value={formatLeaveApproverRemarks(record.reviewRemarks)}
              />
            ) : null}
            <Divider sx={{ my: 0.75 }} />
            <InfoField label="Type" value={leaveTypeLabel(record.leaveType as string)} />
            <InfoField label="From" value={formatListDate(record.startDate, { empty: "—" })} />
            <InfoField label="To" value={formatListDate(record.endDate, { empty: "—" })} />
            <InfoField label="Source" value={record.source as string} />
            {record.reason ? <InfoField label="Reason" value={record.reason as string} /> : null}
          </InfoSection>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <InfoSection title="Employee">
            {portalOnly ? (
              <InfoField label="Employee Id" value={String(record.employeeId ?? "—")} />
            ) : (
              <>
                <ReferenceField source="employeeId" reference="employees" link={false}>
                  <TextField source="name" />
                </ReferenceField>
                <InfoField label="Employee Id" value={String(record.employeeId)} />
                <Button
                  component={RouterLink}
                  to={staffShowPath(record.employeeId as number)}
                  size="small"
                  variant="outlined"
                  sx={{ alignSelf: "flex-start", mt: 0.5 }}
                >
                  Open staff dashboard
                </Button>
              </>
            )}
          </InfoSection>

          {record.reviewedAt ? (
            <Box sx={{ mt: 2 }}>
              <InfoSection title="Review">
                <InfoField label="Reviewed by" value={record.reviewedByName || "—"} />
                <InfoField label="Reviewed via" value={(record.reviewedVia as string) || "—"} />
                <InfoField
                  label="Reviewed at"
                  value={formatDisplayDateTime(record.reviewedAt, { empty: "—" })}
                />
                <InfoField
                  label={remarksLabel}
                  value={formatLeaveApproverRemarks(record.reviewRemarks)}
                />
              </InfoSection>
            </Box>
          ) : null}

          <Box sx={{ mt: 2 }}>
            <InfoSection title="Timestamps">
              <InfoField
                label="Created"
                value={formatDisplayDateTime(record.createdAt, { empty: "—" })}
              />
              <InfoField
                label="Updated"
                value={formatDisplayDateTime(record.updatedAt, { empty: "—" })}
              />
            </InfoSection>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const OfficeLeaveRequestShowBody = () => {
  const { record } = useShowContext<LeaveRecord>();
  const leaveShowMerge = useLeaveShowMerge();
  if (!record) return null;
  const displayRecord = leaveShowMerge?.patchedRecord
    ? { ...record, ...leaveShowMerge.patchedRecord }
    : record;
  return <LeaveRequestShowBody record={displayRecord} />;
};

/** Portal-only show: loads via GET employeeLeaveRequests/me/:id (no employees.view). */
function PortalLeaveRequestShow() {
  const { id } = useParams<{ id: string }>();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<LeaveRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchRecord = useCallback(async () => {
    if (!id) {
      setError("Leave request not found");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: response } = await callApi({
        location: `employeeLeaveRequests/me/${id}`,
        method: "GET",
      });
      const body = response as { rows?: LeaveRecord[] };
      const row = Array.isArray(body?.rows) ? (body.rows[0] ?? null) : null;
      if (!row) {
        setError("Leave request not found");
        setRecord(null);
      } else {
        setRecord(row);
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setError("Leave request not found");
      } else {
        notify(leaveApiErrorMessage(err, "Failed to load leave request"), { type: "error" });
        setError("Failed to load leave request");
      }
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    void fetchRecord();
  }, [fetchRecord]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error || !record) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title="Leave request" />
        <Typography color="text.secondary">{error ?? "Leave request not found"}</Typography>
        <Button component={RouterLink} to="/myAttendance" sx={{ mt: 2 }} variant="outlined">
          Back to My dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Title title="Leave request" />
      <LeaveActionsBar record={record} portalOnly onRecordUpdated={(row) => setRecord(row)} />
      <LeaveRequestShowBody record={record} portalOnly />
      <Box sx={{ px: 1, pb: 2 }}>
        <Button component={RouterLink} to="/myAttendance" size="small" variant="text">
          Back to My dashboard
        </Button>
      </Box>
    </Box>
  );
}

function OfficeLeaveRequestShow(props: ShowProps) {
  const [patchedRecord, setPatchedRecord] = useState<LeaveRecord | null>(null);
  const mergeRecord = useCallback((row: LeaveRecord) => setPatchedRecord(row), []);

  return (
    <LeaveShowMergeContext.Provider value={{ patchedRecord, mergeRecord }}>
      <Show {...props} actions={<OfficeLeaveActions />}>
        <OfficeLeaveRequestShowBody />
      </Show>
    </LeaveShowMergeContext.Provider>
  );
}

/**
 * Leave show: office users use employees.view getOne;
 * portal-only (myAttendance.view without employees.view) uses GET …/me/:id and cancel-only actions.
 */
const EmployeeLeaveRequestShow = (props: ShowProps) => {
  const portalOnly = usePortalOnlyLeaveViewer();
  if (portalOnly) {
    return <PortalLeaveRequestShow />;
  }
  return <OfficeLeaveRequestShow {...props} />;
};

export default EmployeeLeaveRequestShow;
